import type { ObjectId } from 'mongodb';

import { EquipmentType } from './EnergyBackup';
import type { Meta } from './Entities';
import { ProgramType } from './ProgramTypes';
import { GeneratorEquipmentType, HVACEquipmentType, WaterHeaterEquipmentType } from './SystemDesigns';
import { WarrantyPaymentFrequency, WarrantyType } from './Warranties';

export enum PricingSettingsType {
  constructionAdders = 'constructionAdders',
  energyCommunity = 'energyCommunity',
  holdbacks = 'holdbacks',
  lowAndMiddleIncome = 'lowAndMiddleIncome',
  pricingRanges = 'pricingRanges',
  taxCreditModifiers = 'taxCreditModifiers',
  ppwAdjustment = 'ppwAdjustment',
  domesticContent = 'domesticContent',
  warranty = 'warranty',
}

export enum ConstructionAdderType {
  arbitrageBattery = 'arbitrageBattery',
  arbitrageBatteryCost = 'arbitrageBatteryCost',
  backupBattery = 'backupBattery',
  backupBatteryKwh = 'backupBatteryKwh',
  electricalUpgrade = 'electricalUpgrade',
  backupBatteryPriceCap = 'backupBatteryPriceCap',
  batteryMinimumCapacity = 'batteryMinimumCapacity',
  solarReadiness = 'solarReadiness',
  arbitrageBatteryPriceCap = 'arbitrageBatteryPriceCap',
}

export enum EnergyCommunityType {
  coalTract = 'coalTract',
  combined = 'combined',
  fossilFuel = 'fossilFuel',
}

export enum DomesticContentType {
  domesticContent = 'domesticContent',
}

export enum ItcModifierFormatType {
  percent = 'percent',
  perWatt = 'perWatt',
}

export enum ITCModifierType {
  energyCommunity = 'energyCommunity',
  domesticContent = 'domesticContent',
}

export enum AdderValueType {
  additionalKwhRateBand = 'additionalKwhRateBand',
  rawCost = 'rawCost',
}

export interface AdderOption {
  adderType: ConstructionAdderType;
  enabled: boolean;
  valueType: AdderValueType;
  value?: number;
}
//TODO: Update when admin pricing gets merged in
export interface EquipmentOption {
  equipmentType: EquipmentType;
  priceCaps: EnergyBackupCaps[];
}

export type PaymentFactorAdder = {
  escalationRate: number;
  value: number;
}[];

export type BatteryPriceCap = {
  batteryId: ObjectId;
  maxPrice: number;
};

export interface EnergyBackupCaps {
  manufacturer: string;
  maxPrice: number;
  maxTermLength: number;
}

/** Marginal per-battery caps for one Product Line. `caps[i]` caps battery `i + 1`; the last value extends upward. */
export interface EnergyBackupCapSchedule {
  productLineId: ObjectId;
  caps: number[];
  /** Flat allowance for non-hardware electrical work, added once per project however many batteries it has. */
  electricalAdder?: number;
}

export interface PricingSettingsDocument {
  id: ObjectId;
  type: PricingSettingsType;
  name: string;
  value: Record<string, any>;
  meta?: Meta;
  programType: ProgramType;
}

export interface PriceSheetRangesSettingsDocument extends PricingSettingsDocument {
  value: {
    pvOnlyMin: number;
    pvOnlyCeiling: number;
    zeroEscalatorCeiling?: number;
    absoluteCeiling: number;
    escalationRateMin: number;
    escalationRateMax: number;
    state: string;
    organizationName: string;
  };
}

export interface ConstructionAdderSettingsDocument extends PricingSettingsDocument {
  value: {
    adderType: ConstructionAdderType;
    kwhAdderBand?: number;
    paymentFactors?: PaymentFactorAdder;
    backupBatteryPriceCaps?: BatteryPriceCap[];
    arbitrageBatteryPriceCaps?: BatteryPriceCap[];
    maxAmount?: number;
    minCapacity?: number;
    energyBackupCapSchedules?: EnergyBackupCapSchedule[];
  };
}

export interface EnergyBackupCapsSettingsDocument extends PricingSettingsDocument {
  value: {
    adderType: ConstructionAdderType;
    energyBackupCaps: EnergyBackupCaps[];
    energyBackupCapSchedules?: EnergyBackupCapSchedule[];
  };
}

export type EquipmentWarrantyValue = {
  [equipmentType in HVACEquipmentType]: number;
};

export type WaterHeaterEquipmentWarrantyValue = {
  [equipmentType in WaterHeaterEquipmentType]: number;
};

export type GeneratorEquipmentWarrantyValue = {
  [equipmentType in GeneratorEquipmentType]: number;
};

export interface AccessoryWarrantyValue {
  minCost: number;
  maxCost: number;
  warrantyCost: number;
}

export interface ZoningWarrantyValue {
  minZones: number;
  maxZones: number;
  warrantyCost: number;
  perZone?: boolean;
}

export interface DuctlessMiniSplitWarrantyValue {
  indoorOutdoorCost: number;
  additionalIndoorCost: number;
}

export enum WarrantyPricingStatus {
  active = 'active',
  archived = 'archived',
}

export interface WarrantyPricingSettingsDocument extends PricingSettingsDocument {
  value: {
    warrantyType: WarrantyType;
    status: WarrantyPricingStatus;
    warrantyProvider: string;
    paymentFrequency: WarrantyPaymentFrequency;
    equipment?: EquipmentWarrantyValue;
    waterHeaterEquipment?: WaterHeaterEquipmentWarrantyValue;
    generatorEquipment?: GeneratorEquipmentWarrantyValue;
    accessories?: AccessoryWarrantyValue[];
    zoning?: ZoningWarrantyValue[];
    ductlessMiniSplit?: DuctlessMiniSplitWarrantyValue;
    createdAt: Date;
    archivedAt: Date;
  };
}

export enum ItcModifierSystemType {
  pvOnly = 'pvOnly',
  storageOnly = 'storageOnly',
  pvAndStorage = 'pvAndStorage',
}

export const ItcModifierSystemTypeLabels: Record<ItcModifierSystemType, string> = {
  [ItcModifierSystemType.pvOnly]: 'PV',
  [ItcModifierSystemType.storageOnly]: 'Storage',
  [ItcModifierSystemType.pvAndStorage]: 'PV and Storage',
};

export interface EnergyCommunitySetting {
  type: EnergyCommunityType;
  value: number;
  format: ItcModifierFormatType;
  systemType?: ItcModifierSystemType;
}

export interface PpwAdjustmentSetting {
  amount: number;
}

export interface DomesticContentSetting {
  modifier: {
    format: ItcModifierFormatType;
    value: number;
  };
  holdback: {
    format: ItcModifierFormatType;
    value: number;
  };
  systemType: ItcModifierSystemType;
}

export type ModifiersPricingSettings = PpwAdjustmentSetting;
export type ItcModifiersPricingSettings = EnergyCommunitySetting[] | DomesticContentSetting[];
export interface ModifierSettingsDocument extends PricingSettingsDocument {
  value: ModifiersPricingSettings;
}

export interface ItcModifierSettingsDocument extends PricingSettingsDocument {
  value: ItcModifiersPricingSettings;
}

export interface HoldbackSettingsDocument extends PricingSettingsDocument {
  value: {
    amount: number;
  };
}
